import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Smartphone, Mail, Globe, Hash, Facebook, Youtube, ChevronRight, Phone, Clock } from 'lucide-react';
import { normalizeImageUrl } from '../utils/imageUtils';
import { getSettingAddress, getSettingMapUrl } from '../utils/branchUtils';
import { useData } from '../context/DataContext';

const Footer = ({ onOpenBooking }) => {
    const { settings, serviceGroups, postCategories } = useData();
    return (
        <footer className="mt-0 overflow-hidden">
            {/* ── Mobile Glowing Action Bar (Mobile Only) ── */}
            <div className="md:hidden bg-black py-6 flex justify-center gap-8 items-center relative z-20 overflow-visible">
                <a href={`tel:${settings?.hotline || ""}`} className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white icon-glow-red animate-pulse-thin active:scale-90 transition-transform">
                    <Phone size={22} strokeWidth={2.5} />
                </a>

                <a href={getSettingMapUrl(settings?.address)} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-slate-900 icon-glow-white active:scale-90 transition-transform">
                    <MapPin size={22} strokeWidth={2.5} />
                </a>

                <Link to="/lien-he" className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-white icon-glow-red active:scale-90 transition-transform">
                    <ChevronRight size={24} strokeWidth={3} />
                </Link>
            </div>

            {/* ── Segmented Action Bar (Desktop only) ── */}
            <div className="bg-segmented-bar hidden md:flex relative z-10">
                <div className="slanted-segment segment-black text-white px-20">
                    <div className="flex items-center gap-4">
                        <Phone size={24} className="text-red-600 animate-pulse" />
                        <div className="flex flex-col">
                            <span className="text-[10px] uppercase font-mono-tech opacity-60">Hotline 24/7</span>
                            <span className="text-xl font-black">{settings?.hotline || "0568 05 0505"}</span>
                        </div>
                    </div>
                </div>

                <div className="slanted-segment segment-white text-slate-900 border-x border-slate-100">
                    <a href={getSettingMapUrl(settings?.address)} target="_blank" rel="noreferrer" className="flex items-center gap-4 no-underline text-inherit group">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-all">
                            <MapPin size={18} />
                        </div>
                        <span className="text-sm font-black uppercase tracking-widest">Chỉ dẫn địa điểm</span>
                    </a>
                </div>

                <div className="slanted-segment segment-red text-white">
                    <Link to="/lien-he" className="flex items-center gap-4 no-underline text-white hover:text-white group">
                        <span className="text-sm font-black uppercase tracking-[0.3em] group-hover:tracking-[0.5em] transition-all">Liên hệ ngay</span>
                        <ChevronRight size={20} className="group-hover:translate-x-2 transition-transform" />
                    </Link>
                </div>
            </div>

            {/* ── Main Footer Space ── */}
            <div className="bg-[#050505] text-white pt-24 pb-12 relative">
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-full h-full opacity-5 pointer-events-none">
                    <div className="bg-tech-grid absolute inset-0 invert" />
                </div>

                <div className="container max-w-7xl mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
                        {/* Col 1: Brand */}
                        <div className="flex flex-col gap-10">
                            <Link to="/" className="flex items-center gap-4 group">
                                <div className="p-2 bg-white rounded-2xl">
                                    <img
                                        src={settings?.logo ? normalizeImageUrl(settings.logo) : "/assets/images/logo-garage.png"}
                                        alt="Logo"
                                        className="h-12 w-auto object-contain"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-lg font-black tracking-tighter uppercase font-mono-tech text-red-600">
                                        {settings?.siteName || "Trung Anh"}
                                    </span>
                                    <span
                                        className="text-[10px] font-light italic text-blue-500 leading-[1.1] tracking-wide"
                                    >
                                        {settings?.slogan}
                                    </span>
                                </div>
                            </Link>

                            <p className="text-white text-sm leading-relaxed text-justify">
                                {settings?.footerDescription || "Chúng tôi cam kết mang đến những giải pháp bảo trì đẳng cấp, hiện đại và khoa học nhất cho xế yêu của bạn."}
                            </p>

                            <div className="flex gap-4">
                                {[
                                    { icon: <Facebook size={20} strokeWidth={2.5} />, href: settings?.facebookUrl },
                                    { icon: <Youtube size={20} strokeWidth={2.5} />, href: settings?.youtubeUrl },
                                    {
                                        icon: (
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M9 12a4 4 0 1 0 4 4V4" />
                                                <path d="M13 4a4 4 0 0 0 5 4" />
                                            </svg>
                                        ),
                                        href: settings?.tiktokUrl
                                    }
                                ].map((s, i) => (
                                    <a
                                        key={i}
                                        href={s.href}
                                        className="w-10 h-10 rounded-full bg-slate-500 flex items-center justify-center hover:bg-primary transition-all shadow-lg text-white"
                                    >
                                        {s.icon}
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Col 2: Contact Info */}
                        <div className="flex flex-col gap-8">
                            <h5 className="text-xs uppercase font-mono-tech font-black tracking-[0.4em] text-red-600">Connect_Center</h5>
                            <ul className="flex flex-col gap-6 list-none p-0 m-0">
                                <li className="flex gap-4 items-start group">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                                        <MapPin size={14} className="text-red-600 group-hover:text-white" />
                                    </div>
                                    <span className="text-sm text-white group-hover:text-white transition-colors">
                                        {getSettingAddress(settings?.address)}
                                    </span>
                                </li>
                                <li className="flex gap-4 items-start group">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                                        <Smartphone size={14} className="text-red-600 group-hover:text-white" />
                                    </div>
                                    <span className="text-sm text-white group-hover:text-white transition-colors">{settings?.hotline || "0568 05 0505"}</span>
                                </li>
                                <li className="flex gap-4 items-start group">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                                        <Mail size={14} className="text-red-600 group-hover:text-white" />
                                    </div>
                                    <span className="text-sm text-white group-hover:text-white transition-colors">
                                        {settings?.email || "info@trunganhgarage.vn"}
                                    </span>
                                </li>
                                <li className="flex gap-4 items-start group">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                                        <Clock size={14} className="text-red-600 group-hover:text-white" />
                                    </div>
                                    <span className="text-sm text-white group-hover:text-white transition-colors">
                                        8:00 - 17:30 (Tất cả các ngày trong tuần)
                                    </span>
                                </li>
                                <li className="flex gap-4 items-start group">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                                        <Hash size={14} className="text-red-600 group-hover:text-white" />
                                    </div>
                                    <span className="text-sm text-white group-hover:text-white transition-colors">
                                        MST: 0110527448
                                    </span>
                                </li>
                                <li className="flex gap-4 items-start group">
                                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 transition-colors">
                                        <Globe size={14} className="text-red-600 group-hover:text-white" />
                                    </div>
                                    <a href="http://ototrunganh.com/" target="_blank" rel="noreferrer" className="text-sm text-white group-hover:text-red-400 transition-colors no-underline">
                                        ototrunganh.com
                                    </a>
                                </li>
                            </ul>
                        </div>

                        {/* Col 3: Services Summary */}
                        <div className="flex flex-col gap-8">
                            <h5 className="text-xs uppercase font-mono-tech font-black tracking-[0.4em] text-red-600">Dịch Vụ</h5>
                            <ul className="flex flex-col gap-4 list-none p-0 m-0">
                                {serviceGroups?.slice(0, 15).map((group, i) => (
                                    <li key={i} className="group">
                                        <Link to={`/dich-vu-nhom/${group.slug}`} className="text-sm text-white group-hover:text-red-600 transition-all flex items-center gap-2 no-underline">
                                            <div className="w-1 h-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                                            {group.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Col 4: Blog Categories & System Status */}
                        <div className="flex flex-col gap-8">
                            <h5 className="text-xs uppercase font-mono-tech font-black tracking-[0.4em] text-red-600">Blog & Kiến Thức</h5>
                            <ul className="flex flex-col gap-4 list-none p-0 m-0">
                                {postCategories?.filter(c => c.title === 'BLOG' || c.parentName === 'BLOG').map((cat, i) => (
                                    <li key={i} className="group">
                                        <Link to={`/blog/danh-muc/${cat._id || cat.id}`} className="text-sm text-white group-hover:text-red-600 transition-all flex items-center gap-2 no-underline">
                                            <div className="w-1 h-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                                            {cat.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex flex-col gap-4 mt-auto">
                                <h5 className="text-xs uppercase font-mono-tech font-black tracking-[0.4em] text-red-600">System_Status</h5>
                                <div className="p-6 bg-white/5 border border-white/10 rounded-2xl relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-2 h-2 bg-green-500 rounded-full animate-pulse m-3" />
                                    <div className="flex flex-col gap-2">
                                        <span className="text-[10px] font-mono-tech text-white/60">Service availability</span>
                                        <span className="text-xl font-black text-white">99.9%</span>
                                        <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mt-2">
                                            <div className="w-full h-full bg-red-600" />
                                        </div>
                                        <span className="text-[8px] font-mono-tech text-white/40 mt-2 uppercase tracking-widest">Operation System v2.0.1</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <img src="/assets/logoSaleNoti.png" alt="BCT" className="h-10 opacity-50 contrast-125 hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Copyright Segment */}
                <div className="mt-24 pt-8 border-t border-white/5">
                    <div className="container max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="text-[11px] font-mono-tech text-white uppercase tracking-widest">
                            © 2026 TRUNG ANH GARAGE / SCIENTIFIC CARE SYSTEM
                        </div>
                        <div className="flex gap-8 text-[11px] font-mono-tech text-white uppercase tracking-widest">
                            <Link to="/" className="hover:text-white transition-colors">Privacy_Policy</Link>
                            <Link to="/" className="hover:text-white transition-colors">Terms_Of_Service</Link>
                        </div>
                    </div>
                </div>
            </div>

        </footer>
    );
};

export default Footer;
